import { useEffect, useRef, useState } from "react";
import type { ReelItem } from "@/content/work-reel";
import { cn } from "@/lib/utils";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

/**
 * En ruta i arbetsreelen. Film visar YouTube-poster som basläge och monterar
 * en tyst loop först när rutan syns i vyn — aldrig vid reducerad rörelse.
 * Foto renderar en vanlig bild som öppnar lightboxen vid klick.
 */
export function ReelTile({
  item,
  aspect = "video",
  eager = false,
  fill = false,
  onPhotoClick,
}: {
  item: ReelItem;
  aspect?: "video" | "portrait" | "square";
  eager?: boolean;
  fill?: boolean;
  onPhotoClick?: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting);
      },
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const aspectClass = fill
    ? "aspect-square md:aspect-auto md:h-full md:min-h-0"
    : aspect === "portrait"
      ? "aspect-[3/4]"
      : aspect === "square"
        ? "aspect-square"
        : "aspect-video";

  if (item.kind === "photo") {
    // Hela bilden ska synas: rutan följer bildens egna proportioner när den
    // står fritt, och letterboxas (contain) när mosaiken låser radhöjden.
    const ratio = item.w && item.h ? `${item.w} / ${item.h}` : undefined;
    return (
      <button
        type="button"
        data-reel-tile={item.id}
        onClick={() => onPhotoClick?.(item.id)}
        className={cn(
          "group relative w-full overflow-hidden rounded-2xl border border-white/10 bg-surface/40",
          fill ? "h-full min-h-[220px] md:min-h-0" : "",
        )}
        style={fill || !ratio ? undefined : { aspectRatio: ratio }}
      >
        <img
          src={item.src}
          alt={item.alt}
          width={item.w}
          height={item.h}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain p-1 transition-transform duration-700 ease-out group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-background/70 px-2.5 py-1 text-[10px] uppercase tracking-widest text-foreground/80">
          Foto
        </span>
      </button>
    );
  }

  const play = inView && !reduced;
  const poster = `https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`;

  return (
    <div
      ref={ref}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-white/10 bg-surface/40",
        aspectClass,
      )}
    >
      <img
        src={poster}
        alt={item.title}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
      />
      {play ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${item.youtubeId}&controls=0&modestbranding=1&playsinline=1&rel=0&disablekb=1&iv_load_policy=3&fs=0`}
          title={item.title}
          allow="autoplay; encrypted-media; picture-in-picture"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 h-[175%] w-[175%] -translate-x-1/2 -translate-y-1/2 border-0"
        />
      ) : null}
      <a
        href={`https://www.youtube.com/watch?v=${item.youtubeId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 flex items-end p-3 text-left"
      >
        <span className="rounded-full bg-background/70 px-2.5 py-1 text-[10px] uppercase tracking-widest text-foreground/80">
          Film
        </span>
        <span className="sr-only">{item.title} — öppna filmen på YouTube</span>
      </a>
    </div>
  );
}
